import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Instituto BR Cultural';
const SITE_URL = 'https://institutobrcultural.org';
const DEFAULT_IMAGE = `${SITE_URL}/og-default.jpg`;

/**
 * SEOHead — meta tags + Open Graph + Twitter Card + Schema.org JSON-LD
 * Usage:
 *   <SEOHead title="..." description="..." />
 *   <SEOHead article={{ headline, author, date, image, slug, excerpt }} />
 */
const SEOHead = ({ title, description, image, url, article, noindex }) => {
  const pageTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const pageDesc = description || 'Instituto BR Cultural — cultura, economia criativa e inovacao no Brasil.';
  const pageImage = image || DEFAULT_IMAGE;
  const pageUrl = url || SITE_URL;

  const jsonLd = article ? {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.headline,
    author: { '@type': 'Person', name: article.author || 'Steven Phil' },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo192.png` }
    },
    datePublished: article.date,
    dateModified: article.updated || article.date,
    image: article.image || pageImage,
    description: article.excerpt || pageDesc,
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/artigos/${article.slug}` }
  } : {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL
  };

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <link rel="canonical" href={pageUrl} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={article ? 'article' : 'website'} />
      <meta property="og:title" content={title || SITE_NAME} />
      <meta property="og:description" content={pageDesc} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="pt_BR" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title || SITE_NAME} />
      <meta name="twitter:description" content={pageDesc} />
      <meta name="twitter:image" content={pageImage} />

      {/* Article-specific */}
      {article && <meta property="article:published_time" content={article.date} />}
      {article && <meta property="article:author" content={article.author || 'Steven Phil'} />}
      {article?.category && <meta property="article:section" content={article.category} />}

      {/* JSON-LD */}
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
};

export default SEOHead;
